// OP-3605 — Detalhe de uma solicitação minha (ESL2): itens, eventos,
// histórico visível ao solicitante, responder informação e cancelar. O
// servidor filtra por autor: trocar o id na URL devolve "não encontrada".
import { FC, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useEstoqueMinhaSolicitacao } from '../../../../hooks/estoque'
import { cancelarEstoqueSolicitacao, responderInformacaoEstoqueSolicitacao } from '../../../../Service/estoque'
import { TIPO_DEMANDA_LABEL } from '../../../../types/estoque'
import { BD, Button, Card, Field, Input, PageSubtitle, PageTitle, Spinner, StatusBadge, Table } from '../../styles'
import { fmtDate, fmtDateTime } from '../../statusUi'
import { tratarErro } from '../erros'
import { STATUS_SOLICITACAO_UI, ehFinal } from '../statusSolicitacao'
import { cancelamentoBloqueadoPorEntrega, podeCancelarPedido } from '../Pedido/regras'
import { faltaMotivo } from '../formato'
import { exibirDocumento } from '../documento'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

export const EstoqueMinhaSolicitacao: FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const idNum = Number(id) || null
  const { data: s, isLoading, isError, isUnauthorized, mutate } = useEstoqueMinhaSolicitacao(idNum)
  const [resposta, setResposta] = useState('')
  const [motivo, setMotivo] = useState('')
  const [confirmandoCancelar, setConfirmandoCancelar] = useState(false)
  const [busy, setBusy] = useState(false)

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  const executar = async (acao: () => Promise<any>, ok: string) => {
    setBusy(true)
    try {
      await acao()
      toast.success(ok)
      setResposta(''); setMotivo(''); setConfirmandoCancelar(false)
      await mutate()
    } catch (err) {
      const t = tratarErro(err)
      if (t.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(t.texto)
      if (t.tipo === 'estado') await mutate()
    } finally {
      setBusy(false)
    }
  }

  if (isLoading) return <Card><div style={{ textAlign: 'center', padding: 24 }}><Spinner /></div></Card>
  if (isError || !s) {
    return (
      <>
        <PageTitle>Solicitação</PageTitle>
        <Card><div style={{ padding: 24, color: BD.grayText }}>Solicitação não encontrada. <Button $variant="secondary" onClick={() => navigate('/interno/estoque/minhas')}>Voltar</Button></div></Card>
      </>
    )
  }

  const ui = STATUS_SOLICITACAO_UI[s.status] ?? { bg: '#F3F4F6', color: '#374151', label: s.status }
  const podeCancelar = !ehFinal(s.status) && ['EXPEDIDA', 'ATENDIDA_PARCIAL'].indexOf(s.status) < 0 && podeCancelarPedido(s)
  const impConfirmar = faltaMotivo(motivo, 'Escreva o motivo do cancelamento: ele fica no histórico do pedido.')
  const bloqueadoPorEntrega = cancelamentoBloqueadoPorEntrega(s)

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <PageTitle>{s.numero} <StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge></PageTitle>
          <PageSubtitle>{TIPO_DEMANDA_LABEL[s.tipoDemanda] ?? s.tipoDemanda} · para {s.detentorNome ?? '—'} · aberta em {fmtDateTime(s.criadoEm)} · prazo {fmtDate(s.prazoEm)}</PageSubtitle>
        </div>
        <Button $variant="secondary" onClick={() => navigate('/interno/estoque/minhas')}>‹ Minhas solicitações</Button>
      </div>

      {s.contato && (s.contato.nome || s.contato.documento) && (
        <Card style={{ padding: 12, marginBottom: 16, fontSize: 13 }}>
          <span style={{ color: BD.grayText }}>Destinatário:</span> {s.contato.nome ?? '—'}
          {s.contato.telefone ? ` · ${s.contato.telefone}` : ''}
          {s.contato.documento ? ` · ${exibirDocumento(s.contato.documento)}` : ''}
        </Card>
      )}

      {s.status === 'AGUARDANDO_INFO' && (
        <Card style={{ padding: 16, marginBottom: 16, borderColor: BD.red }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>A logística perguntou</div>
          <div style={{ marginBottom: 8 }}>{s.historico.find((h) => h.para === 'AGUARDANDO_INFO')?.mensagem ?? '—'}</div>
          <Field>
            <label>Sua resposta</label>
            <Input value={resposta} maxLength={1000} onChange={(e) => setResposta(e.target.value)} />
          </Field>
          <Button disabled={busy || !resposta.trim()} onClick={() => executar(() => responderInformacaoEstoqueSolicitacao(s.id, resposta.trim()), 'Resposta enviada')} style={{ marginTop: 8 }}>Responder</Button>
        </Card>
      )}

      {s.motivoRecusa && (
        <Card style={{ padding: 12, marginBottom: 16 }}><b>Motivo da recusa:</b> {s.motivoRecusa}</Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        <Card>
          <div style={{ padding: '8px 12px', fontWeight: 600 }}>Itens pedidos</div>
          <Table>
            <thead><tr><th>Item</th><th style={{ textAlign: 'right' }}>Qtd</th></tr></thead>
            <tbody>{s.itens.map((i, k) => <tr key={k}><td>{i.nome}{i.unidadeMedida ? ` (${i.unidadeMedida})` : ''}</td><td style={{ textAlign: 'right' }}>{i.quantidade}</td></tr>)}</tbody>
          </Table>
        </Card>
        <Card>
          <div style={{ padding: '8px 12px', fontWeight: 600 }}>Eventos</div>
          <Table>
            <thead><tr><th>Evento</th><th>Data</th></tr></thead>
            <tbody>{s.eventos.map((e, k) => <tr key={k}><td>{e.eventoNome}</td><td>{fmtDate(e.eventoData)}</td></tr>)}</tbody>
          </Table>
        </Card>
      </div>

      {s.observacao && <Card style={{ padding: 12, marginTop: 16 }}><b>Observação:</b> {s.observacao}</Card>}

      <Card style={{ marginTop: 16 }}>
        <div style={{ padding: '8px 12px', fontWeight: 600 }}>Histórico</div>
        <Table>
          <thead><tr><th>Quando</th><th>Situação</th><th>Mensagem</th></tr></thead>
          <tbody>
            {s.historico.map((h, k) => {
              const hu = STATUS_SOLICITACAO_UI[h.para]
              return <tr key={k}><td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{fmtDateTime(h.criadoEm)}</td><td>{hu ? <StatusBadge $bg={hu.bg} $color={hu.color}>{hu.label}</StatusBadge> : h.para}</td><td>{h.mensagem ?? ''}</td></tr>
            })}
          </tbody>
        </Table>
      </Card>

      {bloqueadoPorEntrega && (
        <Card style={{ padding: 16, marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Cancelar a solicitação</div>
          <div style={{ fontSize: 13, color: BD.grayText }}>Este pedido já teve entrega ou tem remessa a caminho: não dá mais para cancelar. Fale com a logística para encerrar o que falta.</div>
        </Card>
      )}

      {podeCancelar && (
        <Card style={{ padding: 16, marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Cancelar a solicitação</div>
          <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>Só antes da entrega. O que já foi separado volta ao estoque. Cancelar é definitivo: para pedir de novo, abra outra solicitação.</div>
          {!confirmandoCancelar ? (
            <Button $variant="secondary" disabled={busy} onClick={() => setConfirmandoCancelar(true)}>Cancelar solicitação</Button>
          ) : (
            <>
              <Field>
                <label htmlFor="motivo-cancelamento-req">Motivo do cancelamento</label>
                <Input id="motivo-cancelamento-req" value={motivo} maxLength={500} onChange={(e) => setMotivo(e.target.value)} />
              </Field>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <Button $variant="secondary" disabled={busy} onClick={() => { setConfirmandoCancelar(false); setMotivo('') }}>Voltar</Button>
                <Button $variant="secondary" disabled={busy || !!impConfirmar} onClick={() => executar(() => cancelarEstoqueSolicitacao(s.id, motivo.trim()), 'Solicitação cancelada')}>Confirmar o cancelamento de {s.numero}</Button>
              </div>
              {impConfirmar && <div style={{ fontSize: 12, color: BD.grayText, marginTop: 8 }}>{impConfirmar}</div>}
            </>
          )}
        </Card>
      )}
    </>
  )
}

export default EstoqueMinhaSolicitacao
